import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';

/**
 * Global "Scheduled Maintenance" banner shown at the top of every page.
 *
 * HOW TO SCHEDULE THE MAINTENANCE WINDOW
 * --------------------------------------
 * Edit `environment.maintenance` in src/environments/environment(.prod).ts:
 *   - enabled: true/false   master on/off switch
 *   - from / to: JST date & time as 'YYYY-MM-DD HH:mm', e.g. '2026-06-25 22:00'
 *
 * When enabled, the banner shows and hides itself automatically once the
 * maintenance END time has passed.
 */
@Component({
  selector: 'app-maintenance-banner',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (showBanner) {
      <div class="maintenance-banner">
        <div class="maintenance-banner__title">{{ 'scheduled_maintenance' | translate }}</div>
        <div class="maintenance-banner__text">
          {{ 'maintenance_banner_message' | translate:{ from: formattedFrom, to: formattedTo } }}
        </div>
      </div>
    }
  `,
  styles: [`
    .maintenance-banner {
      width: 100%;
      background-color: #f26430;
      color: #ffffff;
      text-align: center;
      padding: 12px 16px;
      position: sticky;
      top: 0;
      z-index: 99999;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    }
    .maintenance-banner__title {
      font-weight: 700;
      font-size: 16px;
      margin-bottom: 2px;
    }
    .maintenance-banner__text {
      font-size: 14px;
      line-height: 1.5;
    }
  `]
})
export class MaintenanceBannerComponent implements OnInit, OnDestroy {
  // Maintenance window comes from the environment file.
  private readonly maintenance = environment.maintenance;

  showBanner = false;
  formattedFrom = '';
  formattedTo = '';
  private timer: any;

  constructor(private cdr: ChangeDetectorRef, private datePipe: DatePipe) {}

  ngOnInit(): void {
    // Pre-format the window in JST so the translated message can interpolate them.
    this.formattedFrom = this.datePipe.transform(this.toJstIso(this.maintenance?.from), 'yyyy/MM/dd HH:mm', '+0900') || '';
    this.formattedTo = this.datePipe.transform(this.toJstIso(this.maintenance?.to), 'yyyy/MM/dd HH:mm', '+0900') || '';
    this.updateVisibility();
    // Re-evaluate periodically so the banner auto-hides once the window ends
    // without requiring a page reload.
    this.timer = setInterval(() => this.updateVisibility(), 30000);
  }

  ngOnDestroy(): void {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  private updateVisibility(): void {
    if (!this.maintenance?.enabled || !this.maintenance.from || !this.maintenance.to) {
      this.setVisible(false);
      return;
    }

    const now = Date.now();
    const from = new Date(this.toJstIso(this.maintenance.from)).getTime();
    const to = new Date(this.toJstIso(this.maintenance.to)).getTime();

    // Visible ONLY while enabled AND the current time is within the window
    // [from, to). Before it starts or after it ends, the banner stays hidden.
    this.setVisible(!isNaN(from) && !isNaN(to) && now >= from && now < to);
  }

  /**
   * Converts a JST 'YYYY-MM-DD HH:mm' value into an ISO string with the +09:00
   * offset so it is parsed/displayed as Japan time regardless of the browser's
   * local timezone. Values that already carry an offset/'Z' are returned as-is.
   */
  private toJstIso(value: string | undefined): string {
    if (!value) {
      return '';
    }
    const trimmed = value.trim();
    if (/([zZ]|[+-]\d{2}:?\d{2})$/.test(trimmed)) {
      return trimmed;
    }
    let s = trimmed.replace(' ', 'T');
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s)) {
      s += ':00';
    }
    return s + '+09:00';
  }

  private setVisible(visible: boolean): void {
    if (this.showBanner !== visible) {
      this.showBanner = visible;
      this.cdr.markForCheck();
    }
  }
}
